import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AlertController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import Auth0Cordova from '@auth0/cordova';
import { TranslateConfigService } from './services/translate/translate-config.service';
import { ProfileService } from './services/profile/profile.service';
import { NotificationService } from './services/notification/notification.service';
import { GeoLocationService } from './services/geolocation/geolocation.service';
import { FirebaseService } from './services/firebase-analytics/firebase.service';
import { filter, map } from 'rxjs/operators';
import { TitleService } from './services/title/title.service';

const GA_TRACKING_ID_ALL = 'G-KHFCLYN67X';
const GA_TRACKING_ID_FILTERED = 'G-41ESZH5RSM';
const GA_TRACKING_ID_TEST = 'G-3LB1V1N87N';

const defaultPageTitle = 'Default | Webapp | SiWeGO';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {

  constructor(
    private alert: AlertController,
    private modal: ModalController,
    private platform: Platform,
    private popover: PopoverController,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private translate: TranslateConfigService,
    private profile: ProfileService,
    private notification: NotificationService,
    private geolocation: GeoLocationService,
    private router: Router,
    private location: Location,
    private firebaseService: FirebaseService,
    private activatedRoute: ActivatedRoute,
    private titleService: TitleService,
  ) {

    this.initializeApp();

    this.translate.setBrowserLanguage();
  }

  ngOnInit(): void {
    this.setPageTitle();
  }

  initializeApp() {
    this.platform.ready()
      .then(() => {
        this.platform.backButton.subscribeWithPriority(1, () => this.handleBackButton());
        this.statusBar.styleDefault();
        this.statusBar.backgroundColorByHexString('#f4f5f8');
        this.splashScreen.hide();

        this.setupAnalytics();

        this.initNotification();

        this.geolocation.init();

        (window as any).handleOpenURL = (url: string) => {
          if (this.platform.is('cordova')) {
            Auth0Cordova.onRedirectUri(url);
          }
        };
      });
  }

  setupAnalytics() {
    if (this.platform.is('cordova')) {
      this.trackAppAnalytics();
    } else {
      this.trackWebAnalytics();
    }
  }

  trackWebAnalytics() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd){
          gtag('config', `${GA_TRACKING_ID_ALL}`, { 'page_path': event.urlAfterRedirects } );
          gtag('config', `${GA_TRACKING_ID_FILTERED}`, { 'page_path': event.urlAfterRedirects } );
          gtag('config', `${GA_TRACKING_ID_TEST}`, { 'page_path': event.urlAfterRedirects } );
        }
      }
    )
  }

  trackAppAnalytics() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd){
          this.firebaseService.firebaseScreenName(event.urlAfterRedirects);
        }
      }
    )
  }
  
  initNotification() {
    if (!this.platform.is('cordova')) { return; }

    this.notification.init();

    this.profile.getProfile()
      .then(profile => {
        if (profile && profile.id) {
          this.notification.setTag(profile.id);
        }
      })
      .catch(err => console.log('No profile for notification tag'));
  }

  async handleBackButton() {
    const modal = await this.modal.getTop();
    if (modal) {
      return modal.dismiss();
    }

    const alert = await this.alert.getTop();
    if (alert) {
      return alert.dismiss();
    }

    const popover = await this.popover.getTop();
    if (popover) {
      return popover.dismiss();
    }

    if (this.router.url === '/profile') {
      await this.router.navigate(['/tabs/home']);
    } else if (this.router.url === '/tabs/home') {
      navigator[`app`].exitApp();
    } else {
      this.location.back();
    }
  }

  setPageTitle(): void {     
    const lang = this.translate.getCurrentLanguage();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let child = this.activatedRoute.firstChild;
       
        if (!child) {
          switch (lang) {
            case 'it' : return (this.activatedRoute.snapshot.data.title.it || defaultPageTitle);
            case 'en' : return (this.activatedRoute.snapshot.data.title.en || defaultPageTitle);
            case 'de' : return (this.activatedRoute.snapshot.data.title.de || defaultPageTitle);
            case 'fr' : return (this.activatedRoute.snapshot.data.title.fr || defaultPageTitle);
            case 'is' : return (this.activatedRoute.snapshot.data.title.is || defaultPageTitle);
            case 'ro' : return (this.activatedRoute.snapshot.data.title.ro || defaultPageTitle);
            case 'pl' : return (this.activatedRoute.snapshot.data.title.pl || defaultPageTitle);
            default: return (this.activatedRoute.snapshot.data.title.en || defaultPageTitle);
          }
        }
        
        while (child.firstChild) {
          child = child.firstChild;
        }

        if (child.snapshot.data.title) {
          switch (lang) {
            case 'it' : return (child.snapshot.data.title.it || defaultPageTitle);
            case 'en' : return (child.snapshot.data.title.en || defaultPageTitle);
            case 'de' : return (child.snapshot.data.title.de || defaultPageTitle);
            case 'fr' : return (child.snapshot.data.title.fr || defaultPageTitle);
            case 'is' : return (child.snapshot.data.title.is || defaultPageTitle);
            case 'ro' : return (child.snapshot.data.title.ro || defaultPageTitle);
            case 'pl' : return (child.snapshot.data.title.pl || defaultPageTitle);
            default: return (child.snapshot.data.title.en || defaultPageTitle);
          }
        }
      })
      ).subscribe((title: string) => this.titleService.setTitle(title));
  }
}
